/**
 * Converts a hex color to HSL
 * @param hex - Hex color string (e.g., '#ce1c5b')
 * @returns Object with h, s, l values
 */
export function hexToHSL(hex: string): { h: number; s: number; l: number } {
  // Remove the hash if present
  const sanitizedHex = hex.replace('#', '')

  // Convert hex to RGB
  const r = parseInt(sanitizedHex.substring(0, 2), 16) / 255
  const g = parseInt(sanitizedHex.substring(2, 4), 16) / 255
  const b = parseInt(sanitizedHex.substring(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  }
}

/**
 * Converts HSL to hex color
 * @param h - Hue (0-360)
 * @param s - Saturation (0-100)
 * @param l - Lightness (0-100)
 * @returns Hex color string
 */
export function hslToHex(h: number, s: number, l: number): string {
  const hDecimal = h / 360
  const sDecimal = s / 100
  const lDecimal = l / 100

  let r, g, b

  if (s === 0) {
    r = g = b = lDecimal
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      if (t < 1 / 6) return p + (q - p) * 6 * t
      if (t < 1 / 2) return q
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
      return p
    }

    const q =
      lDecimal < 0.5
        ? lDecimal * (1 + sDecimal)
        : lDecimal + sDecimal - lDecimal * sDecimal
    const p = 2 * lDecimal - q

    r = hue2rgb(p, q, hDecimal + 1 / 3)
    g = hue2rgb(p, q, hDecimal)
    b = hue2rgb(p, q, hDecimal - 1 / 3)
  }

  const toHex = (x: number) => {
    const hex = Math.round(x * 255).toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
}

/**
 * Generates a color scale from 100 to 900 based on a base color (500)
 * @param baseColorHex - The base color in hex format (this will be 500)
 * @returns Object with color scale from 100 to 900
 */
export function generateColorScale(
  baseColorHex: string
): {
  [key: string]: string
} {
  const baseHSL = hexToHSL(baseColorHex)

  // Define lightness adjustments for each scale
  // 500 is the base, lighter numbers = more lightness, darker numbers = less lightness
  const scales = {
    100: { lightnessModifier: 40, saturationModifier: -30 }, // Very light
    200: { lightnessModifier: 30, saturationModifier: -20 }, // Light
    300: { lightnessModifier: 20, saturationModifier: -10 }, // Medium light
    400: { lightnessModifier: 10, saturationModifier: -5 }, // Slightly light
    500: { lightnessModifier: 0, saturationModifier: 0 }, // Base color
    600: { lightnessModifier: -10, saturationModifier: 5 }, // Slightly dark
    700: { lightnessModifier: -20, saturationModifier: 10 }, // Medium dark
    800: { lightnessModifier: -30, saturationModifier: 10 }, // Dark
    900: { lightnessModifier: -40, saturationModifier: 10 } // Very dark
  }

  const colorScale: { [key: string]: string } = {}

  Object.entries(scales).forEach(
    ([key, { lightnessModifier, saturationModifier }]) => {
      const newLightness = Math.max(
        0,
        Math.min(100, baseHSL.l + lightnessModifier)
      )
      const newSaturation = Math.max(
        0,
        Math.min(100, baseHSL.s + saturationModifier)
      )

      colorScale[key] = hslToHex(baseHSL.h, newSaturation, newLightness)
    }
  )

  return colorScale
}

/**
 * Generates both primary and gray color scales for the corporate theme
 * @param params - Object containing primaryColor and grayColor
 * @param params.primaryColor - The primary color (will be used as 500)
 * @param params.grayColor - The gray color (will be used as 500)
 * @returns Object with primary and gray color scales
 */
export function generateCorporateColors({
  primaryColor,
  secondaryColor
}: {
  primaryColor: string
  secondaryColor: string
}) {
  const primaryScale = generateColorScale(primaryColor)
  const secondaryScale = generateColorScale(secondaryColor)

  return {
    primary: primaryColor,
    primary100: primaryScale['100'],
    primary200: primaryScale['200'],
    primary300: primaryScale['300'],
    primary400: primaryScale['400'],
    primary500: primaryScale['500'],
    primary600: primaryScale['600'],
    primary700: primaryScale['700'],
    primary800: primaryScale['800'],
    primary900: primaryScale['900'],
    secondary: secondaryColor,
    secondary100: secondaryScale['100'],
    secondary200: secondaryScale['200'],
    secondary300: secondaryScale['300'],
    secondary400: secondaryScale['400'],
    secondary500: secondaryScale['500'],
    secondary600: secondaryScale['600'],
    secondary700: secondaryScale['700'],
    secondary800: secondaryScale['800'],
    secondary900: secondaryScale['900'],
    white: '#fafaf4',
    bodyBackground: '#f3f1e8'
  }
}
